import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import moment from "moment-timezone";
import Button from "../../VibehutUI/Button";
import { useHistory } from "react-router-dom";
import { StoreContext } from "../../Context/StoreContext";
import {
  createEventFromRequestEvent,
  createRequestEvent,
  createRequestEventFromChat,
  getAllRequestEvents,
  updateRequestEvent,
} from "../../actions/requestEventActions";
import Avatar from "../../VibehutUI/Avatar";
import { getUserData } from "../../services/userService";
import Loader from "../../VibehutUI/Loader";
import UserListModal from "../../VibehutUI/UserListModal";
import SpecificChat from "../chat/SpecificChat";
import { showLoginModal } from "../../actions/uiModalActions";
import { specificChatType } from "../../constants";
import RoomModel from "../../VibehutUI/RoomModal";
import globals from "../../globals";
import { checkMyRoomSubscription } from "../../utils/utils";
import { cancelRequestEvent } from "../../services/requestEventService";
import { singleClaimedRoomRoutes, singleRoomRoutes } from "../../routes";
import { showToast } from "../../actions/toastActions";

function RequestEventCard({
  date,
  room,
  roomOwner,
  placeHolderIndex,
  eventFilter,
}) {
  const { state, dispatch } = useContext(StoreContext);
  const history = useHistory();
  const {
    auth: { user },
    requestEvent: { requestEvents, isCreatingIndex, requestEventChatModal },
    subscription: { mySubscriptions },
  } = state;
  const conatinerRef = React.useRef();
  const [matchedRequestedEvent, setMatchedRequestedEvent] = useState(undefined);
  const [isOpenUserList, setIsOpenUserList] = useState(false);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [isOpenPassword, setIsOpenPassword] = useState(false);
  const [roomPassword, setRoomPassword] = useState("");
  const [isChatButtonClicked, setIsChatButtonClicked] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);

  const checkForPassword = useCallback(() => {
    return (
      room._id &&
      room?.isPasswordProtected &&
      !isPasswordVerified &&
      room?.owner?._id !== user?._id
    );
  }, [
    room?.isPasswordProtected,
    room?.owner?._id,
    isPasswordVerified,
    room._id,
    user?._id,
  ]);

  const checkForNftAccessable = useCallback(() => {
    return room._id && !room?.isAccessible && room?.isNftCollectionRoom;
  }, [room?.isAccessible, room?.isNftCollectionRoom, room._id]);

  const checkForBlocked = useCallback(() => {
    return room._id && room?.blockedUsers?.includes(user?._id);
  }, [room?.blockedUsers, room._id, user?._id]);

  const checkForSubscribedUser = useCallback(() => {
    return (
      room._id &&
      room.isSubscriberRequireApproval &&
      !room.subscriberApprovedUsers?.includes(user?._id)
    );
  }, [
    room.isSubscriberRequireApproval,
    room.subscriberApprovedUsers,
    room._id,
    user?._id,
  ]);

  const checkForIsPaidRoomNotSubscribed = useCallback(() => {
    if (room._id && room?.hasSubscription) {
      if (room?.moderators) {
        for (let i = 0; i < room.moderators.length; i++) {
          if (room.moderators[i].toString() === user?._id.toString())
            return false;
        }
      }
      const mySubscription = checkMyRoomSubscription(
        mySubscriptions,
        room?._id,
      );
      if (!mySubscription) {
        return true;
      }
      return false;
    }

    return false;
  }, [room?._id, room?.hasSubscription, mySubscriptions, room._id]);

  const onRequestSession = async () => {
    try {
      if (!user?.token) {
        return showLoginModal(dispatch);
      }
      const zone = moment.tz.guess();
      if (matchedRequestedEvent) {
        const selfOrganizeCount = room?.selfOrganizeCount || 1;
        if (
          matchedRequestedEvent?.requestedBy?.length >=
          Math.max(selfOrganizeCount - 1, 0)
        ) {
          const requestedBy = matchedRequestedEvent.requestedBy.map(
            (u) => u._id,
          );

          const data = {
            startDate: matchedRequestedEvent.startDate,
            endDate: matchedRequestedEvent.endDate,
            room: matchedRequestedEvent.room,
            requestedEventId: matchedRequestedEvent._id,
            requestedBy: [user?._id, ...requestedBy],
            timezone: zone,
          };
          await createEventFromRequestEvent(
            data,
            user.token,
            dispatch,
            placeHolderIndex,
          );
        } else {
          await updateRequestEvent(
            matchedRequestedEvent._id,
            user.token,
            dispatch,
            placeHolderIndex,
          );
        }
      } else {
        const startDate = new Date(date);
        const endDate = new Date(date);
        endDate.setHours(endDate.getHours() + 1);
        const requestedBy = [user?._id];
        const data = {
          startDate,
          endDate,
          requestedBy,
          room: room._id,
          timezone: zone,
        };
        await createRequestEvent(data, user.token, dispatch, placeHolderIndex);
      }
    } catch (error) {
      showToast(dispatch, "error", JSON.stringify(error), 200);
      console.log("error:", error);
    }
  };

  const onRequestCancel = async () => {
    try {
      setIsCanceling(true);
      if (!user?.token) {
        return showLoginModal(dispatch);
      }
      if (matchedRequestedEvent) {
        await cancelRequestEvent(matchedRequestedEvent._id, user.token);
        await getAllRequestEvents(room._id, dispatch);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsCanceling(false);
    }
  };

  React.useEffect(() => {
    if (isPasswordVerified) {
      if (isChatButtonClicked) {
        onChat();
      } else {
        onRequestSession();
      }
    }
  }, [isPasswordVerified]);

  const isChatRoomOwner = useMemo(() => {
    return room._id && room?.owner?._id === user?._id;
  }, [room?.owner?._id, room._id, user?._id]);

  const handleRequestSession = () => {
    if (!user?.token) {
      return showLoginModal(dispatch);
    }
    if (isChatRoomOwner) {
      onRequestSession();
      return;
    }
    if (checkForPassword()) {
      setIsOpenPassword(true);
    } else if (checkForNftAccessable()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody message={"Non accessible NFT room."} />,
      );
      return;
    } else if (checkForBlocked()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody message={"You are blocked to access this room"} />,
      );
      return;
    } else if (checkForSubscribedUser()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody
          message={"The room host has not approved you yet"}
        />,
      );
      return;
    } else if (checkForIsPaidRoomNotSubscribed()) {
      globals.showModal("Oops!", <ValidationModalBody message={"Paid Room"} />);
      return;
    } else {
      onRequestSession();
    }
  };

  const handleChat = () => {
    setIsChatButtonClicked(true);
    if (isChatRoomOwner) {
      onChat();
      return;
    }
    if (checkForPassword()) {
      setIsOpenPassword(true);
    } else if (checkForNftAccessable()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody message={"Non accessible NFT room."} />,
      );
      return;
    } else if (checkForBlocked()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody message={"You are blocked to access this room"} />,
      );
      return;
    } else if (checkForSubscribedUser()) {
      globals.showModal(
        "Oops!",
        <ValidationModalBody
          message={"The room host has not approved you yet"}
        />,
      );
      return;
    } else if (checkForIsPaidRoomNotSubscribed()) {
      globals.showModal("Oops!", <ValidationModalBody message={"Paid Room"} />);
      return;
    } else {
      onChat();
    }
  };

  const onChat = async () => {
    try {
      if (matchedRequestedEvent) {
        dispatch({
          type: "SET_REQUEST_EVENT_CHAT_MODAL",
          payload: matchedRequestedEvent._id,
        });
      } else {
        const startDate = new Date(date);
        const endDate = new Date(date);
        endDate.setHours(endDate.getHours() + 1);
        const data = {
          startDate,
          endDate,
          room: room._id,
        };
        await createRequestEventFromChat(
          data,
          user.token,
          dispatch,
          placeHolderIndex,
        );
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsChatButtonClicked(false);
    }
  };

  const getMatchRequestedEvent = useCallback(() => {
    if (requestEvents.length > 0) {
      const re = requestEvents.find((item) => {
        const crDay = moment(date).format("D");
        const crHour = moment(date).format("H");
        const stDay = moment(item.startDate).format("D");
        const stHour = moment(item.startDate).format("H");
        if (crDay === stDay && crHour === stHour) {
          return item;
        }
      });
      setMatchedRequestedEvent(re);
    }
  }, [date, requestEvents]);

  useEffect(() => {
    getMatchRequestedEvent();
  }, [getMatchRequestedEvent, requestEvents]);

  const getCardAction = () => {
    const path = room?.slug
      ? singleClaimedRoomRoutes.path.replace(":slug", room.slug)
      : singleRoomRoutes.path.replace(":roomId", room?._id);
    if (isCreatingIndex === placeHolderIndex || isCanceling) {
      return <Loader />;
    }
    if (matchedRequestedEvent?.requestedBy.find((u) => u._id === user?._id)) {
      if (matchedRequestedEvent?.isDisable) {
        return (
          <div className="flex gap-1">
            <Button
              size="vibhut-sm"
              variant="primary"
              onClick={() => {
                history.push(`${path}/events/${matchedRequestedEvent.event}`);
              }}
            >
              View Event
            </Button>
          </div>
        );
      }

      return (
        <div className="flex gap-1">
          <Button size="vibhut-sm" variant="secondary" onClick={handleChat}>
            Chat{" "}
            {matchedRequestedEvent?.specificChatRoom &&
              matchedRequestedEvent?.specificChatRoom[0]?.chatCount > 0 &&
              `(${matchedRequestedEvent.specificChatRoom[0].chatCount})`}
          </Button>
          <Button
            size="vibhut-sm"
            variant="vibhut-primary-outline"
            onClick={(e) => {
              e.stopPropagation();
              onRequestCancel();
            }}
          >
            I'm Interested
          </Button>
        </div>
      );
    }
    return (
      <div className="flex gap-1">
        <Button size="vibhut-sm" variant="secondary" onClick={handleChat}>
          Chat{" "}
          {matchedRequestedEvent?.specificChatRoom &&
            matchedRequestedEvent?.specificChatRoom[0]?.chatCount > 0 &&
            `(${matchedRequestedEvent.specificChatRoom[0].chatCount})`}
        </Button>
        <Button size="vibhut-sm" onClick={handleRequestSession}>
          Interested
        </Button>
      </div>
    );
  };

  const _renderEventTime = () => {
    const startDate = new Date(date);
    const endDate = new Date(date);
    startDate.setMinutes(0);
    endDate.setHours(endDate.getHours() + 1);
    endDate.setMinutes(0);
    const startTime = moment(startDate);
    const endTime = moment(endDate);

    let timeRangeString = "";
    const isAMsame = startTime.format("a") === endTime.format("a");
    if (startTime.format("mm") === "00") {
      timeRangeString =
        timeRangeString + startTime.format(`h${isAMsame ? "" : " A"}`);
    } else {
      timeRangeString =
        timeRangeString + startTime.format(`h:mm${isAMsame ? "" : " A"}`);
    }
    timeRangeString = timeRangeString + "-";
    if (endTime.format("mm") === "00") {
      timeRangeString = timeRangeString + endTime.format("h A");
    } else {
      timeRangeString = timeRangeString + endTime.format("h:mm A");
    }
    const zone = moment.tz.guess();
    const abbr = moment.tz(zone).format("z");

    if (endTime < new Date()) {
      return `${startTime.format("MMM D,")} ${timeRangeString} ${abbr}`;
    }

    return `${timeRangeString} ${abbr}`;
  };

  const getFormattedEvent = useCallback(() => {
    const event = {
      rsvps: [],
    };
    matchedRequestedEvent?.requestedBy.map((re) => {
      event.rsvps.push({ user: re });
    });
    return event;
  }, [matchedRequestedEvent?.requestedBy]);

  const getEventCard = () => {
    return (
      <div ref={conatinerRef}>
        <div className=" border border-lightGray dark:border-matteGray p-4 rounded-xl ">
          <div className="flex space-x-4">
            <div className="w-full flex flex-col space-y-4">
              <div className="flex justify-between">
                <p className="font-medium font-ubuntu text-sm text-darkGray">
                  <span className="uppercase">{_renderEventTime()}</span>
                  <p className="font-medium flex text-base text-darkNight dark:text-frescoWhite">
                    <span className="custom-two-line-clamp">
                      Open Discussion
                    </span>
                  </p>
                </p>
              </div>
              <div className="flex flex-col space-y-2">
                <div className="flex justify-between">
                  <div
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => setIsOpenUserList(true)}
                  >
                    <div>
                      <div className="-space-x-3 flex overflow-hidden">
                        {matchedRequestedEvent?.requestedBy
                          ?.slice(0, 3)
                          .map((user, index) => (
                            <div
                              key={index}
                              className="inline-flex relative overflow-hidden rounded-full border-2 border-white dark:border-navyGray bg-white dark:bg-navyGray "
                            >
                              <Avatar
                                src={getUserData(user).profile_picture}
                                alt="avatar"
                                username={getUserData(user).username}
                                size="xs"
                                className="w-6 h-6 rounded-full"
                              />
                            </div>
                          ))}
                      </div>
                    </div>
                    {matchedRequestedEvent?.requestedBy?.length > 0 && (
                      <div className="text-sm text-darkGray">
                        {matchedRequestedEvent?.requestedBy?.length}{" "}
                        {matchedRequestedEvent?.requestedBy.length > 1
                          ? "requests"
                          : "request"}
                      </div>
                    )}
                  </div>
                  {getCardAction()}
                </div>
              </div>
            </div>
          </div>
        </div>
        {isOpenUserList && (
          <UserListModal
            isOpen={isOpenUserList}
            setIsOpen={setIsOpenUserList}
            event={getFormattedEvent()}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {
        <>
          {eventFilter === "requested" ? (
            <>{!!matchedRequestedEvent && getEventCard()}</>
          ) : (
            <>{getEventCard()}</>
          )}
          {matchedRequestedEvent &&
            requestEventChatModal === matchedRequestedEvent._id && (
              <SpecificChat
                chatItem={{ ...matchedRequestedEvent, owner: roomOwner }}
                type={specificChatType.REQUESTEVENT}
                roomId={room._id}
              />
            )}{" "}
          <RoomModel
            isOpen={isOpenPassword}
            setIsOpen={setIsOpenPassword}
            roomId={room._id}
            setIsPasswordVerified={setIsPasswordVerified}
            roomPassword={roomPassword}
            setRoomPassword={setRoomPassword}
            buttonTitle={isChatButtonClicked ? "Chat" : "Interested"}
          />
        </>
      }
    </>
  );
}

export default RequestEventCard;

const ValidationModalBody = ({ message }) => {
  return (
    <div className="bg-white text-gray-600 dark:text-gray-300 dark:bg-navyGray p-4 relative">
      <p className="text-lg">{message}</p>
    </div>
  );
};
